import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Wallet as WalletIcon } from "lucide-react";

import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Button } from "@/components/ui/button";

/**
 * Compact wallet balance + Add Fund pill — rendered in the global header for
 * partner users. Auto-refreshes when the `wallet-balance-refresh` custom event
 * is dispatched (used by Wallet top-up success / payout success handlers).
 */
export function WalletHeaderPill() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/balance");
      setBalance(typeof data?.balance === "number" ? data.balance : 0);
    } catch {
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("wallet-balance-refresh", handler);
    // Soft poll every 60s so the pill stays roughly current without spamming
    const id = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener("wallet-balance-refresh", handler);
      clearInterval(id);
    };
  }, [refresh]);

  return (
    <div
      className="hidden sm:flex items-center gap-2 rounded-md border border-border bg-card pl-2.5 pr-1 py-1 shadow-sm"
      data-testid="wallet-header-pill"
    >
      <WalletIcon className="h-4 w-4 text-primary" strokeWidth={1.5} />
      <div className="flex flex-col leading-tight">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
          Wallet
        </span>
        <span className="text-xs font-mono font-bold text-foreground" data-testid="wallet-header-balance">
          {loading ? "…" : balance === null ? "—" : formatINR(balance)}
        </span>
      </div>
      <Button
        asChild
        size="sm"
        className="h-7 px-2 ml-1 text-xs"
        data-testid="wallet-header-add-fund"
      >
        <Link to="/wallet?action=topup">
          <Plus className="h-3 w-3 mr-0.5" strokeWidth={2.5} />
          Add fund
        </Link>
      </Button>
    </div>
  );
}
